/**
 * ============================================
 * SOCIALIZA - APK STORE API HELPER
 * ============================================
 * 
 * Funções JavaScript prontas para usar
 * Facilita integração do APK Store no Frontend
 */
import React from 'react';


const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Função auxiliar para fazer requisições à API
 */
async function makeRequest(url, options = {}) {
  const token = localStorage.getItem("token");

  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Erro na requisição");
  }

  return response.json();
}

// ============================================
// LISTAR E BUSCAR APKs
// ============================================

/**
 * Listar todos os APKs com filtros
 */
export async function getAllAPKs(filters = {}) {
  const params = new URLSearchParams();

  if (filters.category) params.append("category", filters.category);
  if (filters.search) params.append("search", filters.search);
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.priceType) params.append("priceType", filters.priceType);
  if (filters.page) params.append("page", filters.page);
  if (filters.limit) params.append("limit", filters.limit);

  const url = `/api/apkstore?${params.toString()}`;
  return makeRequest(url);
}

/**
 * Buscar APKs por texto
 */
export async function searchAPKs(query) {
  return makeRequest(`/api/apkstore/search?q=${encodeURIComponent(query)}`);
}

/**
 * Obter APKs destacados
 */
export async function getFeaturedAPKs(limit = 10) {
  return makeRequest(`/api/apkstore/featured?limit=${limit}`);
}

/**
 * Obter APKs com melhor rating
 */
export async function getTopRatedAPKs(limit = 10) {
  return makeRequest(`/api/apkstore/top-rated?limit=${limit}`);
}

/**
 * Obter APKs mais baixados
 */
export async function getMostDownloadedAPKs(limit = 10) {
  return makeRequest(`/api/apkstore/most-downloaded?limit=${limit}`);
}

/**
 * Obter APKs por categoria
 */
export async function getAPKsByCategory(category, page = 1, limit = 20) {
  return makeRequest(
    `/api/apkstore/category/${category}?page=${page}&limit=${limit}`
  );
}

/**
 * Obter APKs de um desenvolvedor
 */
export async function getDeveloperAPKs(developerId, page = 1, limit = 20) {
  return makeRequest(
    `/api/apkstore/developer/${developerId}?page=${page}&limit=${limit}`
  );
}

// ============================================
// DETALHES E GERENCIAMENTO DE APKs
// ============================================

/**
 * Obter detalhes de um APK
 */
export async function getAPKDetails(apkId) {
  return makeRequest(`/api/apkstore/${apkId}`);
}

/**
 * Criar novo APK
 */
export async function createAPK(apkData) {
  return makeRequest("/api/apkstore", {
    method: "POST",
    body: JSON.stringify(apkData),
  });
}

/**
 * Editar APK
 */
export async function editAPK(apkId, updates) {
  return makeRequest(`/api/apkstore/${apkId}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });
}

/**
 * Deletar APK
 */
export async function deleteAPK(apkId) {
  return makeRequest(`/api/apkstore/${apkId}`, {
    method: "DELETE",
  });
}

// ============================================
// ENGAJAMENTO
// ============================================

/**
 * Curtir/Descurtir APK
 */
export async function likeAPK(apkId) {
  return makeRequest(`/api/apkstore/${apkId}/like`, {
    method: "POST",
  });
}

/**
 * Adicionar/Remover de Favoritos
 */
export async function toggleFavorite(apkId) {
  return makeRequest(`/api/apkstore/${apkId}/favorite`, {
    method: "POST",
  });
}

/**
 * Download de APK
 */
export async function downloadAPK(apkId) {
  return makeRequest(`/api/apkstore/${apkId}/download`, {
    method: "POST",
  });
}

// ============================================
// COMENTÁRIOS E AVALIAÇÕES
// ============================================

/**
 * Criar comentário/review
 */
export async function createComment(apkId, text, rating = null) {
  return makeRequest(`/api/apkstore/${apkId}/comments`, {
    method: "POST",
    body: JSON.stringify({ text, rating }),
  });
}

/**
 * Obter comentários
 */
export async function getAPKComments(apkId, page = 1, limit = 10, sortBy = "newest") {
  return makeRequest(
    `/api/apkstore/${apkId}/comments?page=${page}&limit=${limit}&sortBy=${sortBy}`
  );
}

/**
 * Responder a comentário
 */
export async function replyToComment(commentId, text) {
  return makeRequest(`/api/apkstore/comments/${commentId}/reply`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

/**
 * Curtir comentário
 */
export async function likeComment(commentId) {
  return makeRequest(`/api/apkstore/comments/${commentId}/like`, {
    method: "POST",
  });
}

/**
 * Deletar comentário
 */
export async function deleteComment(commentId) {
  return makeRequest(`/api/apkstore/comments/${commentId}`, {
    method: "DELETE",
  });
}

// ============================================
// MODERAÇÃO
// ============================================

/**
 * Reportar APK
 */
export async function reportAPK(apkId, reason, description = "") {
  return makeRequest(`/api/apkstore/${apkId}/report`, {
    method: "POST",
    body: JSON.stringify({ reason, description }),
  });
}

/**
 * Reportar comentário
 */
export async function reportComment(commentId, reason) {
  return makeRequest(`/api/apkstore/comments/${commentId}/report`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

// ============================================
// ADMIN FUNCTIONS
// ============================================

/**
 * Feature APK (apenas admin)
 */
export async function featureAPK(apkId) {
  return makeRequest(`/api/apkstore/${apkId}/feature`, {
    method: "POST",
  });
}

/**
 * Remover feature (apenas admin)
 */
export async function unfeatureAPK(apkId) {
  return makeRequest(`/api/apkstore/${apkId}/feature`, {
    method: "DELETE",
  });
}

// ============================================
// REACT COMPONENTS EXAMPLES
// ============================================

/**
 * Exemplo 1: Componente de Lista de APKs
 */
export function APKStoreList() {
  const [apks, setApks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [category, setCategory] = React.useState("Games");

  React.useEffect(() => {
    async function loadAPKs() {
      try {
        setLoading(true);
        const data = await getAPKsByCategory(category);
        setApks(data.apks);
      } catch (error) {
        console.error("Erro ao carregar APKs:", error);
      } finally {
        setLoading(false);
      }
    }

    loadAPKs();
  }, [category]);

  if (loading) return <div>Carregando APKs...</div>;

  return (
    <div style={{ padding: "20px" }}>
      <h1>🏪 Loja de APKs</h1>

      <div style={{ marginBottom: "20px" }}>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          style={{ padding: "8px", fontSize: "16px" }}
        >
          <option>Games</option>
          <option>Productivity</option>
          <option>Social</option>
          <option>Communication</option>
          <option>Health</option>
          <option>Music</option>
          <option>Video</option>
          <option>Photography</option>
          <option>News</option>
          <option>Education</option>
          <option>Travel</option>
          <option>Finance</option>
          <option>Shopping</option>
          <option>Utilities</option>
        </select>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
        {apks.map((apk) => (
          <APKCard key={apk._id} apk={apk} />
        ))}
      </div>
    </div>
  );
}

/**
 * Exemplo 2: Card de APK
 */
export function APKCard({ apk }) {
  const [liked, setLiked] = React.useState(false);

  const handleLike = async () => {
    try {
      await likeAPK(apk._id);
      setLiked(!liked);
    } catch (error) {
      console.error("Erro ao curtir:", error);
    }
  };

  const handleDownload = async () => {
    try {
      const result = await downloadAPK(apk._id);
      console.log("APK disponível para download:", result.downloadUrl);
      // Iniciar download
      window.location.href = result.downloadUrl;
    } catch (error) {
      console.error("Erro ao baixar:", error);
    }
  };

  return (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: "8px",
        padding: "15px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <img
        src={apk.icon}
        alt={apk.appName}
        style={{ width: "100%", height: "150px", objectFit: "cover", borderRadius: "4px" }}
      />

      <h3 style={{ marginTop: "10px" }}>{apk.appName}</h3>

      <p style={{ color: "#666", fontSize: "14px" }}>
        por {apk.developer.first_name} {apk.developer.last_name}
      </p>

      <div style={{ marginY: "10px" }}>
        <span style={{ fontSize: "14px", color: "#f39c12" }}>⭐ {apk.rating.average}</span>
        <span style={{ fontSize: "12px", color: "#999", marginLeft: "5px" }}>
          ({apk.rating.totalRatings})
        </span>
      </div>

      <p style={{ fontSize: "12px", color: "#999" }}>
        📥 {apk.downloads} downloads
      </p>

      <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
        <button
          onClick={handleLike}
          style={{
            flex: 1,
            padding: "8px",
            backgroundColor: liked ? "#e74c3c" : "#ecf0f1",
            color: liked ? "white" : "#333",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          {liked ? "❤️ Curtido" : "🤍 Curtir"}
        </button>

        <button
          onClick={handleDownload}
          style={{
            flex: 1,
            padding: "8px",
            backgroundColor: "#3498db",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          📥 Baixar
        </button>
      </div>
    </div>
  );
}

/**
 * Exemplo 3: Componente de Detalhes do APK
 */
export function APKDetails({ apkId }) {
  const [apk, setApk] = React.useState(null);
  const [comments, setComments] = React.useState([]);
  const [newComment, setNewComment] = React.useState("");
  const [newRating, setNewRating] = React.useState(5);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const apkData = await getAPKDetails(apkId);
        setApk(apkData.apk);

        const commentsData = await getAPKComments(apkId);
        setComments(commentsData.comments);
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [apkId]);

  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      alert("Digite um comentário");
      return;
    }

    try {
      await createComment(apkId, newComment, newRating);
      setNewComment("");
      setNewRating(5);

      // Recarregar comentários
      const commentsData = await getAPKComments(apkId);
      setComments(commentsData.comments);
    } catch (error) {
      console.error("Erro ao comentar:", error);
      alert("Erro ao enviar comentário");
    }
  };

  if (loading) return <div>Carregando...</div>;
  if (!apk) return <div>APK não encontrado</div>;

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto", padding: "20px" }}>
      {/* Header */}
      <div style={{ display: "flex", gap: "20px", marginBottom: "30px" }}>
        <img
          src={apk.icon}
          alt={apk.appName}
          style={{ width: "150px", height: "150px", borderRadius: "8px" }}
        />

        <div style={{ flex: 1 }}>
          <h1>{apk.appName}</h1>
          <p style={{ color: "#666" }}>
            Desenvolvido por{" "}
            <strong>
              {apk.developer.first_name} {apk.developer.last_name}
            </strong>
          </p>

          <div style={{ marginY: "10px" }}>
            <span style={{ fontSize: "18px", color: "#f39c12" }}>
              ⭐ {apk.rating.average} ({apk.rating.totalRatings} avaliações)
            </span>
          </div>

          <p style={{ color: "#999" }}>
            📥 {apk.downloads} downloads | 👁️ {apk.views} visualizações
          </p>

          <p style={{ marginTop: "10px", fontWeight: "bold" }}>
            {apk.priceType === "free" ? "🆓 Grátis" : `💰 R$ ${apk.price}`}
          </p>

          <button
            onClick={() => downloadAPK(apkId)}
            style={{
              marginTop: "10px",
              padding: "10px 20px",
              backgroundColor: "#27ae60",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            📥 Baixar APK
          </button>
        </div>
      </div>

      {/* Descrição */}
      <div style={{ marginBottom: "30px" }}>
        <h2>Sobre</h2>
        <p>{apk.description}</p>

        <h3>Notas da Versão</h3>
        <p>{apk.releaseNotes}</p>

        <h3>Informações Técnicas</h3>
        <ul>
          <li>Versão: {apk.version}</li>
          <li>Android mínimo: {apk.minAndroidVersion}</li>
          <li>Android alvo: {apk.targetAndroidVersion}</li>
          <li>Tamanho: {(apk.apkFile.size / 1024 / 1024).toFixed(2)} MB</li>
        </ul>

        {apk.categories && (
          <>
            <h3>Categorias</h3>
            <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
              {apk.categories.map((cat) => (
                <span
                  key={cat}
                  style={{
                    backgroundColor: "#ecf0f1",
                    padding: "5px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                  }}
                >
                  {cat}
                </span>
              ))}
            </div>
          </>
        )}

        {apk.permissions && apk.permissions.length > 0 && (
          <>
            <h3>Permissões</h3>
            <ul>
              {apk.permissions.map((perm) => (
                <li key={perm}>{perm}</li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Screenshots */}
      {apk.screenshots && apk.screenshots.length > 0 && (
        <div style={{ marginBottom: "30px" }}>
          <h2>Screenshots</h2>
          <div style={{ display: "flex", gap: "10px", overflowX: "auto" }}>
            {apk.screenshots.map((screenshot, idx) => (
              <img
                key={idx}
                src={screenshot}
                alt={`Screenshot ${idx + 1}`}
                style={{ height: "300px", borderRadius: "4px" }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Comentários */}
      <div style={{ marginBottom: "30px" }}>
        <h2>Comentários e Avaliações</h2>

        <div
          style={{
            border: "1px solid #ddd",
            borderRadius: "8px",
            padding: "15px",
            marginBottom: "20px",
          }}
        >
          <h3>Deixe sua avaliação</h3>

          <div style={{ marginBottom: "10px" }}>
            <label>Avaliação: </label>
            <select
              value={newRating}
              onChange={(e) => setNewRating(parseInt(e.target.value))}
              style={{ marginLeft: "10px", padding: "5px" }}
            >
              <option value={5}>⭐⭐⭐⭐⭐ Excelente</option>
              <option value={4}>⭐⭐⭐⭐ Muito bom</option>
              <option value={3}>⭐⭐⭐ Bom</option>
              <option value={2}>⭐⭐ Razoável</option>
              <option value={1}>⭐ Ruim</option>
            </select>
          </div>

          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Compartilhe sua experiência com este APK..."
            style={{
              width: "100%",
              minHeight: "100px",
              padding: "10px",
              borderRadius: "4px",
              border: "1px solid #ddd",
              fontFamily: "Arial",
            }}
          />

          <button
            onClick={handleSubmitComment}
            style={{
              marginTop: "10px",
              padding: "10px 20px",
              backgroundColor: "#3498db",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Enviar Avaliação
          </button>
        </div>

        <div>
          {comments.map((comment) => (
            <div
              key={comment._id}
              style={{
                border: "1px solid #eee",
                borderRadius: "4px",
                padding: "10px",
                marginBottom: "10px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <strong>{comment.user.first_name} {comment.user.last_name}</strong>
                <span style={{ color: "#f39c12" }}>
                  {"⭐".repeat(comment.rating || 0)}
                </span>
              </div>
              <p>{comment.text}</p>
              <small style={{ color: "#999" }}>
                {new Date(comment.createdAt).toLocaleDateString("pt-BR")}
              </small>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Exemplo 4: Componente de Upload de APK (para desenvolvedores)
 */
export function APKUploadForm() {
  const [formData, setFormData] = React.useState({
    appName: "",
    packageName: "",
    version: "1.0.0",
    description: "",
    categories: ["Games"],
    priceType: "free",
    price: 0,
  });

  const [uploading, setUploading] = React.useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setUploading(true);
      const result = await createAPK(formData);
      alert("APK publicado com sucesso!");
      console.log(result);
    } catch (error) {
      console.error("Erro ao publicar APK:", error);
      alert("Erro ao publicar APK: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ maxWidth: "500px", margin: "0 auto", padding: "20px" }}>
      <h2>📱 Publicar novo APK</h2>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: "15px" }}>
          <label>Nome do Aplicativo *</label>
          <input
            type="text"
            name="appName"
            value={formData.appName}
            onChange={handleChange}
            required
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
          />
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label>Package Name (ex: com.example.app) *</label>
          <input
            type="text"
            name="packageName"
            value={formData.packageName}
            onChange={handleChange}
            required
            placeholder="com.example.myapp"
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
          />
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label>Versão *</label>
          <input
            type="text"
            name="version"
            value={formData.version}
            onChange={handleChange}
            required
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
          />
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label>Descrição *</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              minHeight: "100px",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          />
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label>Tipo de Preço</label>
          <select
            name="priceType"
            value={formData.priceType}
            onChange={handleChange}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
          >
            <option value="free">Grátis</option>
            <option value="paid">Pago</option>
          </select>
        </div>

        {formData.priceType === "paid" && (
          <div style={{ marginBottom: "15px" }}>
            <label>Preço (R$)</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              min="0"
              step="0.01"
              style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
            />
          </div>
        )}

        <button
          type="submit"
          disabled={uploading}
          style={{
            width: "100%",
            padding: "10px",
            backgroundColor: "#27ae60",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: uploading ? "not-allowed" : "pointer",
            fontSize: "16px",
          }}
        >
          {uploading ? "Publicando..." : "Publicar APK"}
        </button>
      </form>
    </div>
  );
}

export default {
  getAllAPKs,
  searchAPKs,
  getFeaturedAPKs,
  getTopRatedAPKs,
  getMostDownloadedAPKs,
  getAPKsByCategory,
  getDeveloperAPKs,
  getAPKDetails,
  createAPK,
  editAPK,
  deleteAPK,
  likeAPK,
  toggleFavorite,
  downloadAPK,
  createComment,
  getAPKComments,
  replyToComment,
  likeComment,
  deleteComment,
  reportAPK,
  reportComment,
  featureAPK,
  unfeatureAPK,
};
